import { Grid, Button } from '@mui/material';
import './RequestDetail.css';
import RequestPreview from './RequestPreview/RequestPreview';
import RequestConfiguration from './RequestConfiguration/RequestConfiguration';
import RequestActivitiesTable from './RequestActivitiesTable/RequestActivitiesTable';
import RequestEquivalenciesTable from './RequestEquivalenciesTable/RequestEquivalenciesTable';
import RequestComment from './RequestComment/RequestComment';
import RequestReview from './RequestReview/RequestReview';
import RequestDocuments from './RequestDocuments/RequestDocuments';
import AppFile from '../../../models/AppFile';
import EquivalencyRequest from '../../../models/EquivalencyRequest';
import RequestProposalTable from './RequestProposalTable/RequestProposalTable';
import { FileType } from '../../../models/FileType';
import { useRecaptcha } from '../../../shared/google-recaptcha-v3/useRecaptcha';
import googleRecaptchaActions from '../../../shared/google-recaptcha-v3/googleRecaptchaActions';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateFilesRequest } from '../requestSearchSlice';
import { useSelector } from 'react-redux';
import {
  RootState,
  useAppDispatch,
  useAppSelector
} from '../../../shared/redux/store';


interface RequestDocumentProps {
  canSearch: boolean;
  researchRequest: () => void;
  request: EquivalencyRequest;
  studenDocuments: AppFile[];
  activityPrograms?: AppFile[];
}

const RequestDetail = ({
  canSearch,
  researchRequest,
  request,
  studenDocuments
}: RequestDocumentProps) => {

  const hasRejectedEquivalencyStatus = request?.activities?.some(item => item.state === 'REJECTED');

  const { t } = useTranslation();

  const flagRequestReview = false

  const files = useSelector(
    (state: RootState) => state.requestSearch.searchRequest.equivalencies.files
  );
  const editFiles = useSelector(
    (state: RootState) => state.requestSearch.searchRequest.editFiles
  );
  const [documentToUpdate, setDocumentToUpdate] = useState<AppFile[]>([]);
  const isEditFile = useAppSelector((state) => state.requestSearch.isEditFile);
  const { generateToken } = useRecaptcha(googleRecaptchaActions.requestSearch);
  const {
    generalFilesSize,
    academicReportFileSize,
    syllabusFileSize,
    programFileSize
  } = useAppSelector((state) => state.settings);
  const dispatch = useAppDispatch();

  const toSort = (files: AppFile[] | undefined) => {
    if (files !== undefined) {
      return [...files].sort((file1: AppFile, file2: AppFile) => {
        const fileType1 = file1.file_type ?? '';
        const fileType2 = file2.file_type ?? '';
        return fileType1.localeCompare(fileType2);
      });
    }
    return undefined;
  };

  const getMaxSize = (appFile: AppFile): string => {
    switch (appFile.file_type) {
      case 'DU_FRONT':
        return generalFilesSize;
      case 'DU_BACK':
        return generalFilesSize;
      case 'ACADEMIC_REPORT':
        return academicReportFileSize;
      case 'SYLLABUS':
        return syllabusFileSize;
      default:
        return programFileSize;
    }
  };

  const addFile = (appFile: AppFile) => {
    const tempFiles = documentToUpdate.filter((file) => file.id !== appFile.id);
    setDocumentToUpdate([...tempFiles, appFile]);
  };

  const removeFile = (documentId: string) => {
    const tempFiles = documentToUpdate.filter((file) => file.id !== documentId);
    setDocumentToUpdate(tempFiles);
  };

  const reset = () => {
    setDocumentToUpdate([]);
    researchRequest();
  };

  const handlerOnSendFiles = () => {
    const updatedRequest = getUpdatedRequest();
    dispatch(updateFilesRequest({ request: updatedRequest, generateToken })).unwrap().then(() => {
      reset();
    }).catch(error => {
      console.log(error);
      reset();
    });
  }

  const validateIsProgramFileType = (fileType: FileType) => {
    return !(
      fileType === 'DU_BACK' ||
      fileType === 'DU_FRONT' ||
      fileType === 'ACADEMIC_REPORT' ||
      fileType === 'SYLLABUS'
    );
  };

  const getTimeStampDate = (dateString: string): string => {
    const partesFecha = dateString.split('/');
    const fechaAjustadaString =
      partesFecha[1] + '/' + partesFecha[0] + '/' + partesFecha[2];
    const fechaLocal = new Date(fechaAjustadaString).getTime() / 1000;
    return fechaLocal.toString();
  };

  const getUpdatedRequest = (): EquivalencyRequest => {
    const studenDocumentsToSend = request?.files?.map(
      (file) => {
        const updatedDoc = documentToUpdate.find(
          (appFile) =>
            appFile.file_name === file.file_name &&
            appFile.file_type !== undefined &&
            !validateIsProgramFileType(appFile.file_type)
        );
        const isRequestOwnedFile = request?.id
          ? file?.url?.includes(request.id) ?? false
          : false;
        if (updatedDoc) return updatedDoc;
        if (isRequestOwnedFile) return file;
        return undefined;
      }
    ).filter(item => item !== undefined);

    const activitiesTosend = request?.activities?.map((activity) => {
      const timestampApprovalDate = getTimeStampDate(activity.approval_date);
      const updatedActivity = documentToUpdate.find(
        (appFile) =>
          appFile.file_name === activity.program_file?.file_name &&
          appFile.file_type !== undefined &&
          validateIsProgramFileType(appFile.file_type)
      );

      return {
        ...activity,
        approval_date: timestampApprovalDate,
        ...(updatedActivity ? { program_file: updatedActivity } : {})
      };
    });

    const updatedRequest = {
      ...request,
      files: studenDocumentsToSend ?? [],
      activities: activitiesTosend ?? []
    };

    return updatedRequest;
  };

  return (
    <Grid direction={'row'} alignItems={'center'} justifyContent="center" mt={2}>
      <Grid item >
        <RequestPreview />
        <RequestDocuments
          studenDocuments={studenDocuments}
          editFiles={editFiles}
          addFile={addFile}
          removeFile={removeFile}
          files={files}
          isEditFile={isEditFile}
          getMaxSize={getMaxSize}
          toSort={toSort}
        />
        <RequestComment />
        <RequestConfiguration />
        <RequestActivitiesTable
          editFiles={editFiles}
          addFile={addFile}
          removeFile={removeFile}
          isEditFile={isEditFile}
          getMaxSize={getMaxSize}
        />
        <RequestProposalTable />
        <RequestEquivalenciesTable />
        {isEditFile && (
          <Grid container direction={'row'} width={'100%'} justifyContent={'end'} sx={{ marginTop: '20px' }}>
            <Grid item xs={12} md={3} lg={2}>
              <Button
                disabled={documentToUpdate.length === 0}
                className='REQXXI-Search-Button'
                variant='contained'
                onClick={handlerOnSendFiles}
              >
                <>{t('sendFile')}</>
              </Button>
            </Grid>
          </Grid>
        )}
        {
          ((request?.reviewed && request?.review) || (!request.reviewed)) && ((request?.state === 'APPROVED' && hasRejectedEquivalencyStatus) || request?.state === 'REJECTED' || request?.state === 'IN_REVIEW') &&
          <RequestReview canSearch={canSearch} researchRequest={researchRequest} />
        }
      </Grid >
    </Grid>
  );
};

export default RequestDetail;
